import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import Breadcrumb from '../components/Breadcrumb';
import {
  ArrowLeft,
  User,
  Mail,
  Calendar,
  Shield,
  Eye,
  EyeOff,
  BarChart3,
  FileText,
  Loader2,
  Save,
  Lock,
  CheckCircle,
  Building,
  Pencil,
  TrendingUp,
  Settings2,
  ChevronRight,
  MapPin,
  Plus,
  Edit,
  Trash2,
  Clock,
  Crown
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
  const { t } = useTranslation();
  const { user, token, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');
  const [myListings, setMyListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(false);

  // Name change
  const [newName, setNewName] = useState('');
  const [nameLoading, setNameLoading] = useState(false);

  // Email change
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [membershipInfo, setMembershipInfo] = useState(null);

  useEffect(() => {
    if (user) {
      setNewName(user.name || '');
      setNewEmail(user.email || '');
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [summaryRes, settingsRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/settings/public`)
      ]);
      setSummary(summaryRes.data);
      const types = settingsRes.data?.membership_types || [];
      const myType = types.find(t => t.id === user.membership_type);
      if (myType) setMembershipInfo(myType);
      fetchMyListings();
    } catch (e) {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const fetchMyListings = async () => {
    setListingsLoading(true);
    try {
      const res = await axios.get(`${API}/my-listings`, { headers: { Authorization: `Bearer ${token}` } });
      setMyListings(res.data);
    } catch (e) {
      console.error('Error fetching listings:', e);
    } finally {
      setListingsLoading(false);
    }
  };

  const handleDeleteListing = async (listingId) => {
    try {
      await axios.delete(`${API}/listings/${listingId}`, { headers: { Authorization: `Bearer ${token}` } });
      setMyListings(myListings.filter(l => l.id !== listingId));
      toast({ title: t('common.success', 'Success'), description: t('listing.listingDeleted', 'Listing deleted') });
    } catch (err) {
      toast({ title: t('common.error', 'Error'), description: t('admin.listingDeleteError', 'Failed to delete listing'), variant: 'destructive' });
    }
  };

  const handleNameUpdate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setNameLoading(true);
    try {
      await axios.put(`${API}/auth/me/name`, { name: newName.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshAuth();
      toast({ title: t('common.success', 'Success'), description: t('profile.nameUpdated', 'Name updated') });
    } catch (err) {
      toast({ title: t('common.error', 'Error'), description: err.response?.data?.detail || t('common.somethingWentWrong', 'Something went wrong'), variant: 'destructive' });
    } finally {
      setNameLoading(false);
    }
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !emailPassword) return;
    setEmailLoading(true);
    try {
      await axios.put(`${API}/auth/me/email`, { new_email: newEmail.trim(), password: emailPassword }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshAuth();
      toast({ title: t('common.success', 'Success'), description: t('profile.emailUpdated', 'Email updated') });
      setEmailPassword('');
      setShowEmailForm(false);
    } catch (err) {
      toast({ title: t('common.error', 'Error'), description: err.response?.data?.detail || t('common.somethingWentWrong', 'Something went wrong'), variant: 'destructive' });
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({ title: t('common.error', 'Error'), description: t('auth.passwordsDoNotMatch', 'Passwords do not match'), variant: 'destructive' });
      return;
    }
    if (newPassword.length < 4) {
      toast({ title: t('common.error', 'Error'), description: t('auth.passwordMinLength', 'Password must be at least 4 characters'), variant: 'destructive' });
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.put(`${API}/auth/me/password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: t('common.success', 'Success'), description: t('profile.passwordChanged', 'Password changed') });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast({ title: t('common.error', 'Error'), description: err.response?.data?.detail || t('common.somethingWentWrong', 'Something went wrong'), variant: 'destructive' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const getMembershipLabel = (type) => {
    switch (type) {
      case 'bireysel': return t('profile.individual');
      case 'coklu': return t('profile.agency');
      default: return type || t('common.standard', 'Standard');
    }
  };

  const getMembershipColor = (type) => {
    switch (type) {
      case 'bireysel': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'coklu': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-zinc-700 text-zinc-300';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800 text-center max-w-md">
          <h2 className="text-2xl font-bold text-white mb-4">{t('common.loginRequired')}</h2>
          <p className="text-zinc-400 mb-6">{t('common.pleaseLoginToCreate')}</p>
          <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700">
            {t('common.backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'personal', label: t('profile.personalInfo', 'Personal Info'), icon: User },
    { id: 'listings', label: t('listing.myListings', 'My Listings'), icon: FileText },
    { id: 'stats', label: t('analytics.statistics', 'Statistics'), icon: BarChart3 },
  ];

  const usagePercent = membershipInfo
    ? Math.min(((summary?.total_listings || 0) / (membershipInfo.max_listings || 1)) * 100, 100)
    : 0;

  return (
    <div className="min-h-screen bg-zinc-800 animate-fade-in-up">
      <Breadcrumb items={[{ label: t('profile.myProfile'), href: null }]} />

      {/* Hero Profile Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/20 via-emerald-900/10 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-500/10 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />

        <div className="container mx-auto px-4 pt-6 pb-10 relative">
          <Link to="/" className="inline-flex items-center text-zinc-400 hover:text-white transition-colors mb-8 group">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            {t('common.backToHome')}
          </Link>

          <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6">
            {/* Avatar */}
            <div className="relative">
              <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white text-5xl font-bold shadow-2xl shadow-emerald-500/20 ring-4 ring-emerald-500/10">
                {user.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              {user.role === 'admin' && (
                <div className="absolute -top-2 -right-2 bg-purple-500 rounded-lg p-1.5 shadow-lg shadow-purple-500/30">
                  <Shield className="w-4 h-4 text-white" />
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="text-center sm:text-left flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                <h1 className="text-3xl font-bold text-white">{user.name}</h1>
                <Badge className={`${getMembershipColor(user.membership_type)} text-xs w-fit mx-auto sm:mx-0`}>
                  {user.membership_type === 'coklu' ? <Building className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}
                  {getMembershipLabel(user.membership_type)}
                </Badge>
              </div>
              <p className="text-zinc-400 text-sm flex items-center justify-center sm:justify-start gap-1.5">
                <Mail className="w-3.5 h-3.5" />
                {user.email}
              </p>
              <p className="text-zinc-600 text-xs flex items-center justify-center sm:justify-start gap-1.5 mt-2">
                <Calendar className="w-3 h-3" />
                {t('profile.membershipType', 'Membership:')} {new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              {user.subscription_end && (() => {
                const subEnd = new Date(user.subscription_end);
                const now = new Date();
                const daysLeft = Math.ceil((subEnd - now) / (1000 * 60 * 60 * 24));
                const isExpired = subEnd < now;
                return (
                  <p className={`text-xs flex items-center justify-center sm:justify-start gap-1.5 mt-1 ${isExpired ? 'text-red-400' : daysLeft <= 7 ? 'text-amber-400' : 'text-emerald-400'}`}>
                    <Clock className="w-3 h-3" />
                    {isExpired
                      ? t('membership.subscriptionExpired', 'Subscription Expired')
                      : `${daysLeft} ${t('common.daysLeft', 'days left')} (${subEnd.toLocaleDateString('en-US')})`
                    }
                  </p>
                );
              })()}
            </div>

            {/* Quick Stats */}
            {!loading && summary && (
              <div className="flex flex-wrap gap-3">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10 text-center min-w-[100px]">
                  <p className="text-2xl font-bold text-white">{summary.total_listings || 0}</p>
                  <p className="text-zinc-500 text-xs">{t('admin.totalListings', 'Total Listings')}</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10 text-center min-w-[100px]">
                  <p className="text-2xl font-bold text-white">{summary.total_views || 0}</p>
                  <p className="text-zinc-500 text-xs">{t('analytics.totalViews', 'Total Views')}</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10 text-center min-w-[100px]">
                  <p className="text-2xl font-bold text-emerald-400">{summary.views_today || 0}</p>
                  <p className="text-zinc-500 text-xs">{t('analytics.viewsToday', 'Views Today')}</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10 text-center min-w-[100px]">
                  <p className="text-2xl font-bold text-blue-400">{summary.views_this_week || 0}</p>
                  <p className="text-zinc-500 text-xs">{t('analytics.viewsThisWeek', 'This Week')}</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10 text-center min-w-[100px]">
                  <p className="text-2xl font-bold text-pink-400">{summary.total_favorites || 0}</p>
                  <p className="text-zinc-500 text-xs">{t('analytics.totalFavorites', 'Total Favorites')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation & Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* Sidebar Tabs */}
          <div className="lg:w-64 shrink-0">
            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap
                      ${isActive
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shadow-lg shadow-emerald-500/5'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50 border border-transparent'
                      }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? 'text-emerald-400' : 'text-zinc-500'}`} />
                    {tab.label}
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto hidden lg:block" />}
                  </button>
                );
              })}
            </nav>

            {/* Membership Card - Sidebar */}
            {membershipInfo && (
              <div className="hidden lg:block mt-6 bg-gradient-to-br from-zinc-900 to-zinc-900/50 rounded-2xl p-5 border border-zinc-800">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  {user.membership_type === 'coklu' ? <Building className="w-4 h-4 text-purple-400" /> : <User className="w-4 h-4 text-blue-400" />}
                  {membershipInfo.title}
                </h4>

                {/* Subscription Dates */}
                {(() => {
                  const subEnd = user.subscription_end ? new Date(user.subscription_end) : null;
                  const subStart = user.subscription_start ? new Date(user.subscription_start) : null;
                  const now = new Date();
                  const daysLeft = subEnd ? Math.ceil((subEnd - now) / (1000 * 60 * 60 * 24)) : 0;
                  const isExpired = subEnd && subEnd < now;
                  const isFree = membershipInfo.is_free !== false;

                  return (
                    <div className="mb-4 space-y-2">
                      {/* Status Badge */}
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
                        isExpired
                          ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                          : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      }`}>
                        <Crown className="w-3.5 h-3.5" />
                        {isExpired ? t('membership.subscriptionExpired', 'Subscription Expired') : isFree ? t('membership.free', 'Free') + ' ' + t('admin.membership', 'Membership') : t('membership.subscriptionActive', 'Active Membership')}
                      </div>

                      {/* Start Date */}
                      {subStart && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-zinc-500">{t('common.startDate', 'Start')}</span>
                          <span className="text-zinc-300">{subStart.toLocaleDateString('en-US')}</span>
                        </div>
                      )}

                      {/* End Date */}
                      {subEnd && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-zinc-500">{t('common.endDate', 'End')}</span>
                          <span className="text-zinc-300">{subEnd.toLocaleDateString('en-US')}</span>
                        </div>
                      )}

                      {/* Days Left */}
                      {subEnd && !isExpired && (
                        <div className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg ${
                          daysLeft <= 7 ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'
                        }`}>
                          <Clock className="w-3 h-3" />
                          {daysLeft} {t('common.daysLeft', 'days left')}
                        </div>
                      )}
                      {isExpired && (
                        <div className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400">
                          <Clock className="w-3 h-3" />
                          {t('membership.expired', 'Expired')}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <ul className="space-y-1.5 mb-4">
                  {(membershipInfo.description_bullets || []).map((bullet, i) => (
                    <li key={i} className="text-zinc-500 text-xs flex items-start gap-2">
                      <span className="text-emerald-500 mt-0.5">•</span>
                      {bullet}
                    </li>
                  ))}
                </ul>
                <div className="bg-zinc-800/50 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-zinc-500 text-xs">{t('profile.listingLimit', 'Listing Limit')}</span>
                    <span className="text-white text-sm font-bold">
                      {summary ? summary.total_listings || 0 : 0} / {membershipInfo.max_listings || 1}
                    </span>
                  </div>
                  <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        usagePercent >= 100 ? 'bg-red-500' : usagePercent >= 80 ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                      style={{ width: `${usagePercent}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tab Content */}
          <div className="flex-1 min-w-0">

            {/* Personal Info Tab */}
            {activeTab === 'personal' && (
              <div className="space-y-6 animate-fade-in-up">
                {/* Name */}
                <div className="bg-zinc-900/80 backdrop-blur-sm rounded-2xl p-6 border border-zinc-800 hover:border-zinc-700/80 transition-colors">
                  <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-400" />
                    </div>
                    {t('profile.fullName', 'Full Name')}
                  </h3>
                  <form onSubmit={handleNameUpdate}>
                    <div className="flex gap-3">
                      <Input
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        className="bg-zinc-800/50 border-zinc-700 text-white flex-1 focus:border-emerald-500/50 focus:ring-emerald-500/20"
                        placeholder={t('profile.fullNamePlaceholder', 'Your Full Name')}
                        required
                      />
                      <Button
                        type="submit"
                        disabled={nameLoading || newName.trim() === user.name}
                        className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 transition-all"
                      >
                        {nameLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                        {t('common.save', 'Save')}
                      </Button>
                    </div>
                  </form>
                </div>

                {/* Email */}
                <div className="bg-zinc-900/80 backdrop-blur-sm rounded-2xl p-6 border border-zinc-800 hover:border-zinc-700/80 transition-colors">
                  <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-amber-400" />
                    </div>
                    {t('auth.email', 'Email Address')}
                  </h3>
                  {!showEmailForm ? (
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input
                          value={user.email}
                          disabled
                          className="bg-zinc-800/30 border-zinc-700/50 text-zinc-400 pl-10"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowEmailForm(true)}
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white transition-all"
                      >
                        <Pencil className="w-4 h-4 mr-1.5" />
                        {t('common.change', 'Change')}
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleEmailUpdate} className="space-y-3 bg-zinc-800/30 rounded-xl p-4 border border-zinc-700/30">
                      <p className="text-amber-400 text-xs flex items-center gap-1.5 bg-amber-500/5 rounded-lg px-3 py-2">
                        <Shield className="w-3.5 h-3.5 shrink-0" />
                        {t('profile.passwordRequiredForEmail', 'Password is required to change email')}
                      </p>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          className="bg-zinc-800/50 border-zinc-700 text-white pl-10 focus:border-emerald-500/50"
                          placeholder={t('profile.newEmailPlaceholder', 'new@email.com')}
                          required
                        />
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input
                          type="password"
                          value={emailPassword}
                          onChange={(e) => setEmailPassword(e.target.value)}
                          className="bg-zinc-800/50 border-zinc-700 text-white pl-10 focus:border-emerald-500/50"
                          placeholder={t('profile.currentPasswordPlaceholder', 'Your current password')}
                          required
                        />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button
                          type="submit"
                          disabled={emailLoading || !emailPassword || newEmail === user.email}
                          className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40"
                        >
                          {emailLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle className="w-4 h-4 mr-1.5" />}
                          {t('profile.updateEmail', 'Update Email')}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => { setShowEmailForm(false); setNewEmail(user.email); setEmailPassword(''); }}
                          className="border-zinc-700 text-zinc-400 hover:text-white"
                        >
                          {t('common.cancel', 'Cancel')}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>

                {/* Password Change */}
                <div className="bg-zinc-900/80 backdrop-blur-sm rounded-2xl p-6 border border-zinc-800 hover:border-zinc-700/80 transition-colors">
                  <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                      <Lock className="w-4 h-4 text-red-400" />
                    </div>
                    {t('profile.changePassword', 'Change Password')}
                  </h3>
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">{t('profile.currentPassword', 'Current Password')}</Label>
                      <div className="relative">
                        <Input
                          type={showCurrentPw ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="bg-zinc-800/50 border-zinc-700 text-white pr-10 focus:border-emerald-500/50 focus:ring-emerald-500/20"
                          placeholder="••••••••"
                          required
                        />
                        <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors">
                          {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-400 text-sm">{t('profile.newPassword', 'New Password')}</Label>
                        <div className="relative">
                          <Input
                            type={showNewPw ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="bg-zinc-800/50 border-zinc-700 text-white pr-10 focus:border-emerald-500/50 focus:ring-emerald-500/20"
                            placeholder="••••••••"
                            required
                          />
                          <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors">
                            {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400 text-sm">{t('profile.confirmNewPassword', 'Confirm New Password')}</Label>
                        <Input
                          type={showNewPw ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className={`bg-zinc-800/50 border-zinc-700 text-white focus:border-emerald-500/50 focus:ring-emerald-500/20 ${confirmPassword && newPassword !== confirmPassword ? 'border-red-500/50 focus:border-red-500/50' : ''}`}
                          placeholder="••••••••"
                          required
                        />
                        {confirmPassword && newPassword !== confirmPassword && (
                          <p className="text-red-400 text-xs flex items-center gap-1 mt-1">
                            <span className="w-1 h-1 rounded-full bg-red-400" />
                            {t('auth.passwordsDoNotMatch', 'Passwords do not match')}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                      className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 transition-all"
                    >
                      {passwordLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                      {passwordLoading ? t('common.saving', 'Saving...') : t('profile.changePassword', 'Change Password')}
                    </Button>
                  </form>
                </div>
              </div>
            )}

            {/* Listings Tab */}
            {activeTab === 'listings' && (
              <div className="space-y-4 animate-fade-in-up">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-white">{t('listing.myListings', 'My Listings')}</h3>
                  <Link to="/create-listing">
                    <Button className="bg-emerald-600 hover:bg-emerald-500 text-sm">
                      <Plus className="w-4 h-4 mr-1.5" />
                      {t('admin.newListing', 'New Listing')}
                    </Button>
                  </Link>
                </div>

                {listingsLoading ? (
                  <div className="bg-zinc-900/80 rounded-2xl p-12 border border-zinc-800 flex justify-center">
                    <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                  </div>
                ) : myListings.length === 0 ? (
                  <div className="bg-zinc-900/80 rounded-2xl p-12 border border-zinc-800 text-center">
                    <div className="w-16 h-16 bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-zinc-600" />
                    </div>
                    <p className="text-zinc-400 mb-4">{t('listing.noListingsYet', 'You have no listings yet')}</p>
                    <Link to="/create-listing">
                      <Button className="bg-emerald-600 hover:bg-emerald-500">
                        <Plus className="w-4 h-4 mr-1.5" />
                        {t('common.createAListing', 'Create a Listing')}
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {myListings.map((listing) => (
                      <div
                        key={listing.id}
                        className="bg-zinc-900/80 backdrop-blur-sm rounded-xl p-4 border border-zinc-800 hover:border-zinc-700/80 transition-colors flex flex-col sm:flex-row gap-4"
                      >
                        {/* Thumbnail */}
                        <div className="w-full sm:w-24 h-24 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                          {listing.images?.[0] ? (
                            <img src={listing.images[0]} alt={listing.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-600 text-2xl font-bold">
                              {listing.name?.charAt(0)}
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="text-white font-semibold truncate">{listing.name}</h4>
                            {listing.status === 'pending' ? (
                              <Badge className="bg-amber-500/20 text-amber-400 border-0 text-xs shrink-0">{t('listing.pending', 'Pending')}</Badge>
                            ) : listing.status === 'approved' ? (
                              <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-xs shrink-0">{t('listing.published', 'Published')}</Badge>
                            ) : listing.status === 'rejected' ? (
                              <Badge className="bg-red-500/20 text-red-400 border-0 text-xs shrink-0">{t('listing.rejected', 'Rejected')}</Badge>
                            ) : (
                              <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-xs shrink-0">{t('listing.published', 'Published')}</Badge>
                            )}
                          </div>
                          {listing.title && <p className="text-zinc-500 text-sm truncate mb-1">{listing.title}</p>}
                          <div className="flex items-center gap-3 text-xs text-zinc-500">
                            {listing.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {listing.city}
                              </span>
                            )}
                            {listing.category && <span>{listing.category}</span>}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex sm:flex-col gap-2 sm:w-28 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs"
                            onClick={() => navigate(`/escort/${listing.slug || listing.id}`)}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            {t('common.view', 'View')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 border-emerald-700 text-emerald-400 hover:bg-emerald-900/20 text-xs"
                            onClick={() => navigate(`/edit-listing/${listing.id}`)}
                          >
                            <Edit className="w-3.5 h-3.5 mr-1" />
                            {t('common.edit', 'Edit')}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="flex-1 border-red-900 text-red-400 hover:bg-red-900/20 text-xs">
                                <Trash2 className="w-3.5 h-3.5 mr-1" />
                                {t('common.delete', 'Delete')}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">{t('listing.deleteListing', 'Delete Listing')}</AlertDialogTitle>
                                <AlertDialogDescription className="text-zinc-400">
                                  {t('listing.deleteConfirmation', 'Are you sure you want to delete "{{name}}"? This action cannot be undone.', { name: listing.name })}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-zinc-800 text-zinc-300 border-zinc-700">{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDeleteListing(listing.id)}>
                                  {t('common.delete', 'Delete')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Link to full listings page */}
                {myListings.length > 0 && (
                  <Link to="/my-listings">
                    <Button className="w-full bg-zinc-900/80 hover:bg-zinc-700 text-white border border-zinc-700 hover:border-zinc-600 transition-all py-5 rounded-xl group mt-2">
                      <BarChart3 className="w-4 h-4 mr-2 text-emerald-400" />
                      {t('profile.detailedStatsAndManagement', 'Detailed Stats & Management')}
                      <ChevronRight className="w-4 h-4 ml-auto opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </Button>
                  </Link>
                )}
              </div>
            )}

            {/* Stats Tab */}
            {activeTab === 'stats' && (
              <div className="space-y-6 animate-fade-in-up">
                {loading ? (
                  <div className="bg-zinc-900/80 rounded-2xl p-12 border border-zinc-800 flex justify-center">
                    <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                  </div>
                ) : summary ? (
                  <>
                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-zinc-900/80 backdrop-blur-sm rounded-2xl p-6 border border-zinc-800 hover:border-blue-500/20 transition-all group">
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <FileText className="w-5 h-5 text-blue-400" />
                          </div>
                          <TrendingUp className="w-4 h-4 text-zinc-600" />
                        </div>
                        <p className="text-3xl font-bold text-white mb-1">{summary.total_listings || 0}</p>
                        <p className="text-zinc-500 text-sm">{t('admin.totalListings')}</p>
                      </div>

                      <div className="bg-zinc-900/80 backdrop-blur-sm rounded-2xl p-6 border border-zinc-800 hover:border-emerald-500/20 transition-all group">
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Eye className="w-5 h-5 text-emerald-400" />
                          </div>
                          <TrendingUp className="w-4 h-4 text-zinc-600" />
                        </div>
                        <p className="text-3xl font-bold text-white mb-1">{summary.total_views || 0}</p>
                        <p className="text-zinc-500 text-sm">{t('analytics.totalViews')}</p>
                      </div>
                    </div>

                    {/* Membership Usage */}
                    {membershipInfo && (
                      <div className="bg-zinc-900/80 backdrop-blur-sm rounded-2xl p-6 border border-zinc-800">
                        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                            <Crown className="w-4 h-4 text-purple-400" />
                          </div>
                          {t('profile.membershipInfo', 'Membership Info')}
                        </h3>

                        {/* Subscription Info */}
                        {(() => {
                          const subEnd = user.subscription_end ? new Date(user.subscription_end) : null;
                          const subStart = user.subscription_start ? new Date(user.subscription_start) : null;
                          const now = new Date();
                          const daysLeft = subEnd ? Math.ceil((subEnd - now) / (1000 * 60 * 60 * 24)) : 0;
                          const isExpired = subEnd && subEnd < now;
                          const isFree = membershipInfo.is_free !== false;

                          return (
                            <div className="space-y-3 mb-4">
                              {/* Plan Type */}
                              <div className="flex items-center justify-between bg-zinc-800/30 rounded-xl px-4 py-3">
                                <span className="text-zinc-400 text-sm">Plan</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-white font-medium text-sm">{membershipInfo.title}</span>
                                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${isFree ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                    {isFree ? t('membership.free', 'Free') : `${membershipInfo.price}${membershipInfo.currency || '$'}/${t('common.month', 'mo')}`}
                                  </span>
                                </div>
                              </div>

                              {/* Start - End Dates */}
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-zinc-800/30 rounded-xl px-4 py-3">
                                  <span className="text-zinc-500 text-xs block mb-1">{t('common.startDate', 'Start')}</span>
                                  <span className="text-white text-sm font-medium">
                                    {subStart ? subStart.toLocaleDateString('en-US') : '-'}
                                  </span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-xl px-4 py-3">
                                  <span className="text-zinc-500 text-xs block mb-1">{t('common.endDate', 'End')}</span>
                                  <span className="text-white text-sm font-medium">
                                    {subEnd ? subEnd.toLocaleDateString('en-US') : '-'}
                                  </span>
                                </div>
                              </div>

                              {/* Remaining Time */}
                              {subEnd && (
                                <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${
                                  isExpired
                                    ? 'bg-red-500/10 border border-red-500/20'
                                    : daysLeft <= 7
                                      ? 'bg-amber-500/10 border border-amber-500/20'
                                      : 'bg-emerald-500/10 border border-emerald-500/20'
                                }`}>
                                  <Clock className={`w-4 h-4 ${isExpired ? 'text-red-400' : daysLeft <= 7 ? 'text-amber-400' : 'text-emerald-400'}`} />
                                  <span className={`text-sm font-medium ${isExpired ? 'text-red-400' : daysLeft <= 7 ? 'text-amber-400' : 'text-emerald-400'}`}>
                                    {isExpired
                                      ? t('membership.subscriptionExpired', 'Subscription Expired')
                                      : `${daysLeft} ${t('common.daysLeft', 'days left')}`
                                    }
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Listing Usage */}
                        <div className="bg-zinc-800/30 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-zinc-400 text-sm">{t('profile.listingUsage', 'Listing Usage')}</span>
                            <span className="text-white font-semibold">
                              {summary.total_listings || 0} / {membershipInfo.max_listings || 1}
                            </span>
                          </div>
                          <div className="h-3 bg-zinc-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-700 ${
                                usagePercent >= 100 ? 'bg-gradient-to-r from-red-500 to-red-400' : usagePercent >= 80 ? 'bg-gradient-to-r from-amber-500 to-amber-400' : 'bg-gradient-to-r from-emerald-500 to-emerald-400'
                              }`}
                              style={{ width: `${usagePercent}%` }}
                            />
                          </div>
                          <p className="text-zinc-600 text-xs mt-2">
                            {usagePercent >= 100 ? t('profile.limitReached', 'You have reached your limit') : `${Math.round(usagePercent)}% ${t('common.used', 'used')}`}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* View Listings Button */}
                    <Link to="/my-listings">
                      <Button className="w-full bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 hover:border-zinc-600 transition-all py-6 text-base rounded-xl group">
                        <FileText className="w-5 h-5 mr-2 text-emerald-400 group-hover:scale-110 transition-transform" />
                        {t('profile.viewMyListings')}
                        <ChevronRight className="w-5 h-5 ml-auto opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                      </Button>
                    </Link>
                  </>
                ) : (
                  <div className="bg-zinc-900/80 rounded-2xl p-12 border border-zinc-800 text-center">
                    <BarChart3 className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500">{t('analytics.noStatsYet', 'No statistics data available yet')}</p>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
